import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@newfront-insurance/core-ui/v2';
import { useMemo, useState } from 'react';

import type { ShareableEmployee } from './employee-utils';
import { SkillShareUserPicker } from './skill-share-user-picker';
import { useNovaContext } from '../../../providers';

export function AddPeopleDialog({
  open,
  excludedUserUuids,
  onClose,
  onAddPeople,
}: {
  open: boolean;
  excludedUserUuids: Set<string>;
  onClose: () => void;
  onAddPeople: (employees: ShareableEmployee[]) => Promise<boolean>;
}) {
  const { userUuid } = useNovaContext();
  const [selectedEmployees, setSelectedEmployees] = useState<ShareableEmployee[]>([]);
  const [isAddingPeople, setIsAddingPeople] = useState(false);
  const effectiveExcludedUserUuids = useMemo(
    () => new Set([...excludedUserUuids, userUuid]),
    [excludedUserUuids, userUuid],
  );

  const closeDialog = () => {
    setSelectedEmployees([]);
    setIsAddingPeople(false);
    onClose();
  };

  const handleAddEmployee = (employee: ShareableEmployee) => {
    setSelectedEmployees((currentEmployees) => {
      if (currentEmployees.some((currentEmployee) => currentEmployee.userUuid === employee.userUuid)) {
        return currentEmployees;
      }

      return [...currentEmployees, employee];
    });
  };

  const handleRemoveEmployee = (removedUserUuid: string) => {
    setSelectedEmployees((currentEmployees) =>
      currentEmployees.filter((currentEmployee) => currentEmployee.userUuid !== removedUserUuid),
    );
  };

  const handleAddPeople = async () => {
    if (selectedEmployees.length === 0) {
      return;
    }

    setIsAddingPeople(true);
    try {
      const didAddPeople = await onAddPeople(selectedEmployees);
      if (didAddPeople) {
        closeDialog();
      }
    } finally {
      setIsAddingPeople(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isAddingPeople && closeDialog()}>
      <AlertDialogContent className="w-[398px] max-w-[calc(100vw-32px)] gap-5 rounded-[4px] p-6">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-['Open_Sans',sans-serif] text-[16px] leading-6 font-semibold text-[#262D46]">
            Add people
          </AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            Search for teammates and grant them access to this skill.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="custom-skill-share-user-search"
            className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]"
          >
            Add teammates
          </label>
          <SkillShareUserPicker
            excludedUserUuids={effectiveExcludedUserUuids}
            inputId="custom-skill-share-user-search"
            selectedEmployees={selectedEmployees}
            onAddEmployee={handleAddEmployee}
            onRemoveEmployee={handleRemoveEmployee}
          />
        </div>

        <AlertDialogFooter className="flex-row justify-end gap-2">
          <AlertDialogCancel
            disabled={isAddingPeople}
            className="h-9 rounded-[4px] border border-[#BEC7D0] px-4 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#262D46]"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={selectedEmployees.length === 0 || isAddingPeople}
            className="h-9 rounded-[4px] bg-[#0957C3] px-5 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            onClick={(event) => {
              event.preventDefault();
              void handleAddPeople();
            }}
          >
            {isAddingPeople ? 'Adding...' : 'Add people'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
